// src/components/quality/CreateCallAssessmentForm.js
import { useState, useEffect } from 'react';
import api from '../../services/api';
import { toBackendFormat } from '../../hooks/useDateTimeFilter';
import useAuth from "../../hooks/useAuth";

// Переименовываем компонент для ясности

const CreateCallAssessmentForm = ({ projectId, onClose, onAssessmentCreated }) => {
  const { user } = useAuth();
  const [assessmentType, setAssessmentType] = useState('call'); // 'call' или 'ticket'
  const [formData, setFormData] = useState({
    agentId: '',
    agentName: '',
    impact: '',
    group: '',
    srq: '',
    status: '',
    url_sound: '',
    url_ticket: '',
    subject2: '',
    feedback: '',
    assessment_date: (() => {
      const now = new Date();
      // Конвертируем текущее время в UTC+3 для отображения
      const localNow = new Date(now.getTime() + 3 * 60 * 60 * 1000);
      const year = localNow.getFullYear();
      const month = String(localNow.getMonth() + 1).padStart(2, '0');
      const day = String(localNow.getDate()).padStart(2, '0');
      const hours = String(localNow.getHours()).padStart(2, '0');
      const minutes = String(localNow.getMinutes()).padStart(2, '0');
      const seconds = String(localNow.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    })(),
    comment: '',
    criteria: [],
    score: 0,
    maxScore: 0,
    assessment_type: '',
    receipt_channel: '',
    service: '',
    os_specialist: ''
  });

  // Состояния для опций выпадающих списков
  const [fieldOptions, setFieldOptions] = useState({
    operators: [],
    impact: [],
    status: [],
    subject2: [],
    assessment_type: [],
    receipt_channel: [],
    service: [],
    os_specialist: [],
    group: []
  });

  // Состояние для значений полей, от которых зависят критерии
  const [fieldValues, setFieldValues] = useState({
    operators: '',
    impact: '',
    srq: '',
    status: '',
    service: '',
    group: ''
  });
  const [projectName, setProjectName] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Загрузка критериев и опций проекта по API
  useEffect(() => {
    if (!projectId) return;

    const fetchData = async () => {
      try {
        // Получение операторов
        const operatorsResponse = await api.get(`/operators?project_id=${projectId}`);
        const operatorsData = operatorsResponse.data.operators;
        const operators = Array.isArray(operatorsData) ? operatorsData : [];

        setFieldOptions(prev => ({
          ...prev,
          operators: operators
        }));

        // Загрузка критериев
        const criteriaResponse = await api.get(`/quality/criteria?project_id=${projectId}`);
        const criteria = criteriaResponse.data.data || [];

        // Фильтруем только активные (не архивные) критерии
        const activeCriteria = criteria.filter(c => !c.archived);

        // Рассчитываем maxScore как сумму weight всех не архивных критериев
        const maxScore = activeCriteria.reduce((sum, c) => sum + c.weight, 0);

        // Создаем критерии с установкой дефолтных значений
        const criteriaWithDefaults = activeCriteria.map(criterion => ({
          ...criterion,
          score: 0,
          selectedOptionId: criterion.default_option_id || null,
          comment: '',
          options: criterion.options || []
        }));

        // Если есть только один оператор, подставляем его
        if (operators.length === 1) {
          const operator = operators[0];
          setFormData(prev => ({
            ...prev,
            agentId: operator.id,
            agentName: `${operator.surname} ${operator.name}`
          }));
        }
        
        // Начисляем баллы для критериев с дефолтными значениями
        let totalScore = 0;
        criteriaWithDefaults.forEach(criterion => {
          if (criterion.selectedOptionId) {
            const selectedOption = criterion.options.find(opt => opt.id === criterion.selectedOptionId);
            if (selectedOption) {
              criterion.score = selectedOption.score * criterion.weight;
              totalScore += criterion.score;
            }
          }
        });
        
        setFormData((prev) => ({
          ...prev,
          criteria: criteriaWithDefaults,
          score: totalScore,
          maxScore: maxScore
        }));
        
        // Загрузка опций для полей
        const fields = ['agent_name', 'impact', 'status', 'subject2', 'assessment_type', 'service', 'os_specialist', 'receipt_channel', 'group'];
        const optionsPromises = fields.map(field => 
          api.get(`/field-options?project_id=${projectId}&field_name=${field}`)
            .then(res => ({
              field,
              options: res.data?.data || []
            }))
            .catch(err => {
              console.error(`Ошибка загрузки опций для поля ${field}:`, err);
              return { field, options: [] };
            })
        );
        
        const optionsResults = await Promise.all(optionsPromises);
        const optionsMap = {};
        optionsResults.forEach(({ field, options }) => {
          optionsMap[field] = options;
        });
        // Сохраняем operators при обновлении других опций
        setFieldOptions(prev => ({
          ...prev,
          ...optionsMap
        }));
        setProjectName('Проект'); // Имя проекта не возвращается, оставляем заглушку
      } catch (err) {
        console.error('Ошибка загрузки данных:', err);
        setError('Не удалось загрузить данные. Проверьте подключение или обратитесь к администратору.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Обновляем значения зависимых полей
    if (['impact', 'srq', 'status', 'group'].includes(name)) {
      setFieldValues(prev => ({ ...prev, [name]: value }));
    }
  };
  console.log('fieldOptions', fieldOptions)

  const handleTypeChange = (e) => {
    const value = e.target.value;
    setAssessmentType(value);
  };

  const handleCriteriaOptionChange = (criteriaId, optionId) => {
    setFormData(prev => {
      const newCriteria = prev.criteria.map(criterion => {
        if (criterion.id === criteriaId) {
          const selectedOption = criterion.options.find(opt => opt.id === optionId);
          return {
            ...criterion,
            selectedOptionId: optionId,
            score: selectedOption ? selectedOption.score * criterion.weight : 0
          };
        }
        return criterion;
      });

      const totalScore = newCriteria.reduce((sum, c) => sum + c.score, 0);
      return { ...prev, criteria: newCriteria, score: totalScore };
    });
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      // Проверка обязательных полей
      if (!formData.agentName || !formData.agentName.trim()) {
        setError('Пожалуйста, заполните обязательное поле: ФИО сотрудника');
        setSubmitting(false);
        return;
      }
      
      if (!formData.assessment_type || !formData.assessment_type.trim()) {
        setError('Пожалуйста, заполните обязательное поле: Тип оценки');
        setSubmitting(false);
        return;
      }

      if (!formData.receipt_channel || !formData.receipt_channel.trim()) {
        setError('Пожалуйста, заполните обязательное поле: Канал поступления');
        setSubmitting(false);
        return;
      }
      
      if (!formData.impact || !formData.impact.trim()) {
        setError('Пожалуйста, заполните обязательное поле: Impact');
        setSubmitting(false);
        return;
      }
      
      if (!formData.group || !formData.group.trim()) {
        setError('Пожалуйста, заполните обязательное поле: Group');
        setSubmitting(false);
        return;
      }
      
      if (!formData.srq || !formData.srq.trim()) {
        setError('Пожалуйста, заполните обязательное поле: SRQ');
        setSubmitting(false);
        return;
      }
      
      if (!formData.status || !formData.status.trim()) {
        setError('Пожалуйста, заполните обязательное поле: Статус');
        setSubmitting(false);
        return;
      }
      
      if (!formData.url_ticket || !formData.url_ticket.trim()) {
        setError('Пожалуйста, заполните обязательное поле: Ссылка на SRQ (URL)');
        setSubmitting(false);
        return;
      }
      
      if (!formData.subject2 || !formData.subject2.trim()) {
        setError('Пожалуйста, заполните обязательное поле: Subject');
        setSubmitting(false);
        return;
      }
      
      if (!formData.service || !formData.service.trim()) {
        setError('Пожалуйста, заполните обязательное поле: Сервис');
        setSubmitting(false);
        return;
      }
      // Подготовка данных для отправки
      const payload = {
        project_id: projectId,
        assessment_type: formData.assessment_type,
        receipt_channel: formData.receipt_channel,
        agent_id: formData.agentId,
        agent_name: formData.agentName.trim(),
        impact: formData.impact,
        srq: formData.srq,
        status: formData.status,
        service: formData.service,
        group: formData.group,
        // url_sound: assessmentType === 'call' ? formData.url_sound : '',
        url_ticket: formData.url_ticket,
        subject2: formData.subject2,
        feedback: formData.feedback,
        os_specialist: formData.os_specialist,
        assessment_date: formData.assessment_date ? (() => {
          // Создаем дату из строки datetime-local (в локальном времени)
          const localDate = new Date(formData.assessment_date);
          
          // Конвертируем в UTC, вычитая 3 часа (предполагаем, что локальное время MSK)
          const utcDate = new Date(localDate.getTime() - 3 * 60 * 60 * 1000);
          
          // Форматируем дату в ISO 8601 без миллисекунд
          const year = utcDate.getUTCFullYear();
          const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
          const day = String(utcDate.getUTCDate()).padStart(2, '0');
          const hours = String(utcDate.getUTCHours()).padStart(2, '0');
          const minutes = String(utcDate.getUTCMinutes()).padStart(2, '0');
          const seconds = String(utcDate.getUTCSeconds()).padStart(2, '0');
          
          return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        })() : null,
        comment: formData.comment,
        score: formData.score,
        max_score: formData.maxScore,
        criteria: formData.criteria.map(criterion => ({
          criteria_id: criterion.id,
          option_id: criterion.selectedOptionId
          // комментарий к критерию не передаётся в API
        }))
      };

      // Валидация только отображаемых критериев
      const visibleCriteria = formData.criteria.filter(criterion => {
        return !criterion.dependencies || criterion.dependencies.length === 0 ||
          (criterion.dependencies &&
           criterion.dependencies.some(dep =>
             formData[dep.field_name] &&
             dep.field_value === formData[dep.field_name]
           ));
      });

      for (const criterion of visibleCriteria) {
        if (!criterion.selectedOptionId) {
          setError(`Пожалуйста, выберите вариант для критерия "${criterion.name}"`);
          setSubmitting(false);
          return;
        }
      }

      await api.post('/quality/assessment', {...payload, assessor_id: user.id});

      // Успешное сохранение
      // alert(`Оценка сохранена! Общий балл: ${formData.score}/${formData.maxScore}`);
      // Сохраняем ссылку на callback перед закрытием

  const createdCallback = onAssessmentCreated;
  
  // Закрываем форму
  if (onClose) onClose();
  
  // Вызываем callback для обновления списка оценок
  if (createdCallback) {
    createdCallback();
  }
    } catch (err) {
      setError('Не удалось сохранить оценку. Попробуйте ещё раз.');
      console.error('Ошибка сохранения оценки:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-content">
        <h3>Загрузка критериев...</h3>
        <div>Пожалуйста, подождите</div>
      </div>
    );
  }

  return (
    <div className="modal-content">
      <div className="modal-header">
        <h3>Оценка качества</h3>
        <button type="button" className="text-modal-close" onClick={onClose} title="Закрыть">
          ×
        </button>
      </div>

      {error && <div className="form-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="group">Group *</label>
          {fieldOptions.group.length > 0 ? (
              <select
                  id="group"
                  name="group"
                  value={formData.group}
                  onChange={handleChange}
                  required
              >
                <option value="">-- Выберите Group --</option>
                {fieldOptions.group.map(option => (
                    <option key={option.id} value={option.option_value}>
                      {option.option_text}
                    </option>
                ))}
              </select>
          ) : (
              <input
                  id="group"
                  name="group"
                  value={formData.group}
                  onChange={handleChange}
                  placeholder="Введите group"
                  required
              />
          )}
          <small>Поле обязательно для заполнения</small>
        </div>
        <div className="form-group">
          <label htmlFor="receipt_channel">Канал поступления *</label>
          {fieldOptions.receipt_channel.length > 0 ? (
              <select
                  id="receipt_channel"
                  name="receipt_channel"
                  value={formData.receipt_channel}
                  onChange={handleChange}
                  required
              >
                <option value="">-- Выберите канал поступления --</option>
                {fieldOptions.receipt_channel.map(option => (
                    <option key={option.id} value={option.option_value}>
                      {option.option_text}
                    </option>
                ))}
              </select>
          ) : (
              <input
                  type="text"
                  id="receipt_channel"
                  name="receipt_channel"
                  value={formData.receipt_channel}
                  onChange={handleChange}
                  placeholder="Введите канал поступления"
                  required
              />
          )}
          <small>Поле обязательно для заполнения</small>
        </div>
        <div className="form-group">
          <label htmlFor="assessment_type">Тип оценки *</label>
          {fieldOptions.assessment_type.length > 0 ? (
              <select
                  id="assessment_type"
                  name="assessment_type"
                  value={formData.assessment_type}
                  onChange={handleChange}
                  required
              >
                <option value="">-- Выберите тип оценки --</option>
                {fieldOptions.assessment_type.map(option => (
                    <option key={option.id} value={option.option_value}>
                      {option.option_text}
                    </option>
                ))}
              </select>
          ) : (
              <input
                  type="text"
                  id="assessment_type"
                  name="assessment_type"
                  value={formData.assessment_type}
                  onChange={handleChange}
                  placeholder="Введите тип оценки"
                  required
              />
          )}
          <small>Поле обязательно для заполнения</small>
        </div>

        <div className="form-group">
          <label htmlFor="agentName">ФИО сотрудника *</label>
          {fieldOptions.operators.length > 0 ? (
                  <select
                      id="agentName"
                      name="agentName"
                      value={formData.agentName}
                      onChange={(e) => {
                        const selectedName = e.target.value;
                        const selectedOperator = fieldOptions.operators.find(op => `${op.surname} ${op.name}` === selectedName);
                        setFormData(prev => ({
                          ...prev,
                          agentName: selectedName,
                          agentId: selectedOperator ? selectedOperator.id : ''
                        }));
                      }}
                      required
                  >
                    <option value="">-- Выберите сотрудника --</option>
                    {fieldOptions.operators.map(operator => (
                        <option key={operator.id} value={`${operator.surname} ${operator.name}`}>
                          {`${operator.surname} ${operator.name}`}
                        </option>
                    ))}
                  </select>
              )
              : fieldOptions.agent_name.length > 0 ? (
                      <select
                          id="agentName"
                          name="agentName"
                          value={formData.agentName}
                          onChange={handleChange}
                          required
                      >
                        <option value="">-- Выберите сотрудника --</option>
                        {fieldOptions.agent_name.map(option => (
                            <option key={option.id} value={option.option_value}>
                              {option.option_text}
                            </option>
                        ))}
                      </select>
                  )
                  : (
                      <input
                          type="text"
                          id="agentName"
                          name="agentName"
                          value={formData.agentName}
                          onChange={handleChange}
                          placeholder="Введите ФИО проверяемого"
                          required
                      />
                  )}
          <small>Поле обязательно для заполнения</small>
        </div>

        <div className="form-group">
          <label htmlFor="impact">Impact *</label>
          {fieldOptions.impact.length > 0 ? (
              <select
                  id="impact"
                  name="impact"
                  value={formData.impact}
                  onChange={handleChange}
                  required
              >
                <option value="">-- Выберите Impact --</option>
                {fieldOptions.impact.map(option => (
                    <option key={option.id} value={option.option_value}>
                      {option.option_text}
                    </option>
                ))}
              </select>
          ) : (
              <input
                  id="impact"
                  name="impact"
                  value={formData.impact}
                  onChange={handleChange}
                  placeholder="Введите impact"
                  required
              />
          )}
          <small>Поле обязательно для заполнения</small>
        </div>

        <div className="form-group">
          <label htmlFor="url_ticket">Ссылка на SRQ (URL) *</label>
          <input
              type="text"
              id="url_ticket"
              name="url_ticket"
              value={formData.url_ticket}
              onChange={handleChange}
              placeholder="Введите ссылку"
              required
          />
          <small>Поле обязательно для заполнения</small>
        </div>

        <div className="form-group">
          <label htmlFor="srq">SRQ *</label>
          <input
              type="text"
              id="srq"
              name="srq"
              value={formData.srq}
              onChange={handleChange}
              placeholder="Введите SRQ"
              required
          />
          <small>Поле обязательно для заполнения</small>
        </div>

        <div className="form-group">
          <label htmlFor="subject2">Subject *</label>
          {fieldOptions.subject2.length > 0 ? (
              <select
                  id="subject2"
                  name="subject2"
                  value={formData.subject2}
                  onChange={handleChange}
                  required
              >
                <option value="">-- Выберите subject --</option>
                {fieldOptions.subject2.map(option => (
                    <option key={option.id} value={option.option_value}>
                      {option.option_text}
                    </option>
                ))}
              </select>
          ) : (
              <input
                  type="text"
                  id="subject2"
                  name="subject2"
                  value={formData.subject2}
                  onChange={handleChange}
                  placeholder="Введите Subject"
                  required
              />
          )}
          <small>Поле обязательно для заполнения</small>
        </div>

        <div className="form-group">
          <label htmlFor="service">Сервис *</label>
          {fieldOptions.service.length > 0 ? (
              <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
              >
                <option value="">-- Выберите сервис --</option>
                {fieldOptions.service.map(option => (
                    <option key={option.id} value={option.option_value}>
                      {option.option_text}
                    </option>
                ))}
              </select>
          ) : (
              <input
                  type="text"
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  placeholder="Введите сервис"
                  required
              />
          )}
          <small>Поле обязательно для заполнения</small>
        </div>

        <div className="form-group">
          <label htmlFor="status">Статус *</label>
          {fieldOptions.status.length > 0 ? (
              <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
              >
                <option value="">-- Выберите статус --</option>
                {fieldOptions.status.map(option => (
                    <option key={option.id} value={option.option_value}>
                      {option.option_text}
                    </option>
                ))}
              </select>
          ) : (
              <input
                  type="text"
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  placeholder="Введите статус"
                  required
              />
          )}
          <small>Поле обязательно для заполнения</small>
        </div>

        <div className="form-group">
          <label htmlFor="os_specialist">Специалист, давший ОС</label>
          {fieldOptions.os_specialist.length > 0 ? (
              <select
                  id="os_specialist"
                  name="os_specialist"
                  value={formData.os_specialist}
                  onChange={handleChange}
              >
                <option value="">-- Выберите специалиста --</option>
                {fieldOptions.os_specialist.map(option => (
                    <option key={option.id} value={option.option_value}>
                      {option.option_text}
                    </option>
                ))}
              </select>
          ) : (
              <input
                  type="text"
                  id="os_specialist"
                  name="os_specialist"
                  value={formData.os_specialist}
                  onChange={handleChange}
                  placeholder="Введите специалиста, давшего ОС"
              />
          )}
        </div>

        <div className="form-group">
          <label htmlFor="feedback">ОС при обработке</label>
          <textarea
              id="feedback"
              name="feedback"
              className="comment-textarea"
              rows="3"
              value={formData.feedback}
              onChange={handleChange}
              placeholder="Введите ОС при обработке"
          ></textarea>
        </div>

        <div className="form-group">
          <label htmlFor="assessment_date">Дата и время оценки</label>
          <input
              type="datetime-local"
              id="assessment_date"
              name="assessment_date"
              value={formData.assessment_date}
              onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="comment">Комментарий</label>
          <textarea
              id="comment"
              name="comment"
              className="comment-textarea"
              rows="3"
              value={formData.comment}
              onChange={handleChange}
              placeholder="Общий комментарий по оценке"
          ></textarea>
        </div>

        <h4>Критерии оценки *</h4>
        <div className="criteria-list">
          {formData.criteria.map((criterion, index) => {
            // Проверяем, должен ли критерий отображаться
            const shouldShow = !criterion.dependencies || (Array.isArray(criterion.dependencies) && criterion.dependencies.length === 0) ||
                (Array.isArray(criterion.dependencies) &&
                    criterion.dependencies.some(dep =>
                        fieldValues[dep.field_name] &&
                        dep.field_value === fieldValues[dep.field_name]
                    ));

            // Если критерий не должен отображаться
            if (!shouldShow) {
              return null; // Не отображаем критерий
            }

            return (
                <div key={criterion.id} className="criteria-item" style={{
                  borderLeft: criterion.critical ? '5px solid orange' : '5px solid transparent',
                  backgroundColor: criterion.critical ? '#fff8e1' : 'transparent'
                }}>
                  <div
                      className="criteria-label"
                      title={criterion.comment_prompt || 'Нет подсказки'}
                  >
                    <strong>{criterion.name}</strong>
                  </div>
                  <div className="criteria-options">
                    <select
                        value={criterion.selectedOptionId || ''}
                        onChange={(e) => handleCriteriaOptionChange(criterion.id, Number(e.target.value))}
                        required
                        className="option-select"
                        style={{width: '100%', maxWidth: '400px'}}
                    >
                      <option value="">-- Выберите вариант --</option>
                      {criterion.options.map((option) => (
                          <option
                              key={option.id}
                              value={option.id}
                              style={{
                                whiteSpace: 'normal',
                                wordWrap: 'break-word',
                                wordBreak: 'break-word',
                                maxWidth: '380px'
                              }}
                          >
                            {option.text} (баллы: {option.score})
                          </option>
                      ))}
                    </select>
                  </div>
                  {index < formData.criteria.length - 1 && <div className="criteria-divider"></div>}
                </div>
            );
          })}
        </div>


        <div className="modal-actions">
          {onClose && (
              <button
                  type="button"
                  className="btn-cancel"
                  onClick={onClose}
                  disabled={submitting}
              >
                Отмена
              </button>
          )}
          <button
              type="submit"
              className="btn-submit"
              disabled={submitting}
          >
            {submitting ? 'Сохранение...' : 'Сохранить оценку'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateCallAssessmentForm;